import { useCallback, useEffect, useRef, useState } from 'react';
import type { QuestionOption, WireChatMessage } from '@logistics/contracts';
import type { Me } from '../auth/use-me';
import { navigate, replaceRoute } from '../shell/use-route';
import { chatRoute } from './chat-route';
import { useAttachments } from './use-attachments';
import { isBusy } from './live-turn';
import type { LiveTurn } from './live-turn';
import { useChatTurn } from './use-chat-turn';
import { MAX_QUESTION } from './composer';

export interface ChatSession {
  readonly activeId: string | null;
  /** Changes when the person opens another conversation, so the thread starts afresh. */
  readonly epoch: number;
  readonly turn: LiveTurn | null;
  readonly busy: boolean;
  readonly typing: ReadonlySet<string>;
  readonly artifactId: string | null;
  readonly draft: string;
  readonly attachments: ReturnType<typeof useAttachments>;
  readonly setDraft: (text: string) => void;
  readonly open: (id: string | null) => void;
  readonly ask: (text: string) => void;
  /** Asks a question that got no answer again, as it was, without the composer's files. */
  readonly retry: (text: string) => void;
  readonly canAsk: (text: string) => boolean;
  readonly typed: (message: WireChatMessage) => void;
  readonly showArtifact: (id: string | null) => void;
}

/**
 * The chat's moving parts in one place: which conversation is open, the
 * question in flight, which answers are still being typed, and which one's
 * artifact is in the side panel. The open conversation follows the URL
 * (`routeId`): opening one navigates, and a route change — a click, Back,
 * Forward — stops the answer in flight and starts the thread afresh.
 */
export function useChatSession(
  me: Me,
  scope: readonly QuestionOption[],
  routeId: string | null,
): ChatSession {
  const [activeId, setActiveId] = useState<string | null>(routeId);
  const current = useRef(routeId);
  const [typing, setTyping] = useState<ReadonlySet<string>>(new Set());
  const [artifactId, setArtifactId] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [epoch, setEpoch] = useState(0);
  const attachments = useAttachments();

  const onAnswered = useCallback((message: WireChatMessage) => {
    setTyping((all) => new Set(all).add(message.id));
  }, []);
  // A new conversation got its id from the stream: into the URL, without a history entry.
  const adopt = useCallback((id: string) => {
    current.current = id;
    setActiveId(id);
    replaceRoute(chatRoute(id));
  }, []);
  const { turn, send, abort } = useChatTurn(me, activeId, adopt, onAnswered);
  const busy = isBusy(turn);

  useEffect(() => {
    if (routeId === current.current) return;
    current.current = routeId;
    abort();
    setActiveId(routeId);
    setEpoch((n) => n + 1);
    setTyping(new Set());
    setArtifactId(null);
  }, [abort, routeId]);

  const open = useCallback((id: string | null) => navigate(chatRoute(id)), []);

  const canAsk = (text: string): boolean =>
    !busy &&
    !attachments.uploading &&
    scope.length > 0 &&
    text.trim() !== '' &&
    text.trim().length <= MAX_QUESTION;

  const ask = (text: string): void => {
    if (!canAsk(text)) return;
    send({ text: text.trim(), scope, attachments: attachments.refs() });
    setDraft('');
    attachments.clear();
  };

  const retry = (text: string): void => {
    if (busy || scope.length === 0) return;
    send({ text, scope, attachments: [] });
  };

  /** The last word is shown: now the artifact, if the answer has one, opens beside it. */
  const typed = useCallback((message: WireChatMessage) => {
    setTyping((all) => {
      if (!all.has(message.id)) return all;
      const next = new Set(all);
      next.delete(message.id);
      return next;
    });
    if (message.artifact !== undefined) setArtifactId(message.id);
  }, []);

  return {
    activeId,
    epoch,
    turn,
    busy,
    typing,
    artifactId,
    draft,
    attachments,
    setDraft,
    open,
    ask,
    retry,
    canAsk,
    typed,
    showArtifact: setArtifactId,
  };
}
